import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import {
  canManageAdminWorkspace,
  getAdminUser,
  isRoleElevationBlocked,
  replaceAdminUserFacilities,
  updateAdminUserRole,
} from '../../lib/services/adminUsers'

const ROLE_OPTIONS = ['user', 'editor', 'admin', 'superadmin'] as const

export function AdminUserDetailPage() {
  const { userId } = useParams()
  const queryClient = useQueryClient()
  const { viewerContext } = useAuth()
  const viewerRole = viewerContext?.role || 'Surveyor'
  const canManage = canManageAdminWorkspace(viewerRole)
  const [nextRole, setNextRole] = useState('user')
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>([])
  const [isRoleDirty, setIsRoleDirty] = useState(false)
  const [areFacilitiesDirty, setAreFacilitiesDirty] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [isActing, setIsActing] = useState(false)

  const adminUserQueryKey = ['admin-user', userId]
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: adminUserQueryKey,
    queryFn: () => getAdminUser(userId!),
    enabled: canManage && !!userId,
  })

  useEffect(() => {
    if (!data) return
    if (!isRoleDirty) {
      setNextRole(data.user.role)
    }
    if (!areFacilitiesDirty) {
      setSelectedFacilityIds(data.facility_assignments.map((assignment) => assignment.facility_id))
    }
  }, [areFacilitiesDirty, data, isRoleDirty])

  const blockedRoleChange = useMemo(() => {
    if (!data) return true
    return isRoleElevationBlocked(viewerRole, data.user.role, nextRole as 'superadmin' | 'admin' | 'editor' | 'user')
  }, [data, nextRole, viewerRole])

  if (!canManage) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        Admin workspace is restricted to Admin role.
      </div>
    )
  }

  if (isLoading) return <div className="p-10 text-sm text-slate-500">Loading user details...</div>
  if (error || !data) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        <p className="font-semibold">Unable to load user</p>
        <p className="text-sm">{(error as Error | undefined)?.message || 'User not found'}</p>
      </div>
    )
  }

  const onToggleFacility = (facilityId: string) => {
    setAreFacilitiesDirty(true)
    setSelectedFacilityIds((current) =>
      current.includes(facilityId) ? current.filter((item) => item !== facilityId) : [...current, facilityId],
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">{data.user.email}</h1>
        <p className="mt-1 text-sm text-slate-600">User ID: {data.user.user_id}</p>
      </header>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Role Assignment</h2>
        <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-center">
          <select
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm md:w-56"
            value={nextRole}
            onChange={(e) => {
              setIsRoleDirty(true)
              setNextRole(e.target.value)
            }}
          >
            {ROLE_OPTIONS.map((role) => (
              <option key={role} value={role} disabled={isRoleElevationBlocked(viewerRole, data.user.role, role)}>
                {role}
              </option>
            ))}
          </select>
          <button
            className="btn-primary text-sm"
            disabled={isActing || blockedRoleChange || nextRole === data.user.role}
            onClick={async () => {
              setActionError(null)
              setActionSuccess(null)
              setIsActing(true)
              try {
                const updatedUser = await updateAdminUserRole(
                  data.user.user_id,
                  nextRole as 'superadmin' | 'admin' | 'editor' | 'user',
                )
                setIsRoleDirty(false)
                queryClient.setQueryData(adminUserQueryKey, (current: typeof data | undefined) =>
                  current ? { ...current, user: updatedUser } : current,
                )
                setActionSuccess('Role updated.')
                void refetch()
              } catch (err) {
                setActionError((err as Error).message)
              } finally {
                setIsActing(false)
              }
            }}
          >
            Save Role
          </button>
        </div>
        {blockedRoleChange && <p className="mt-2 text-xs text-slate-500">Unauthorized role elevation attempts are blocked.</p>}
      </section>

      <section className="v11-section p-5">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Facility Assignments</h2>
        {data.available_facilities.length === 0 && <p className="mt-2 text-sm text-slate-500">No facilities available for assignment.</p>}
        <div className="mt-3 grid gap-2">
          {data.available_facilities.map((facility) => (
            <label
              key={facility.facility_id}
              className="flex items-center gap-2 rounded-2xl border border-slate-200 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
            >
              <input
                type="checkbox"
                checked={selectedFacilityIds.includes(facility.facility_id)}
                onChange={() => onToggleFacility(facility.facility_id)}
              />
              <span>{facility.facility_name}</span>
            </label>
          ))}
        </div>
        <button
          className="btn-primary mt-3 text-sm"
          disabled={isActing}
          onClick={async () => {
            setActionError(null)
            setActionSuccess(null)
            setIsActing(true)
            try {
              await replaceAdminUserFacilities(data.user.user_id, selectedFacilityIds)
              setAreFacilitiesDirty(false)
              setActionSuccess('Facility assignments updated.')
              await refetch()
            } catch (err) {
              setActionError((err as Error).message)
            } finally {
              setIsActing(false)
            }
          }}
        >
          Save Facility Assignments
        </button>
      </section>

      {actionError && <p className="text-sm text-red-600">{actionError}</p>}
      {actionSuccess && <p className="text-sm text-emerald-700">{actionSuccess}</p>}
      <Link to="/admin/users" className="inline-block text-sm underline">
        Back to users
      </Link>
    </div>
  )
}
